/**
 * El día de calendario en el cliente: un campo `date` de haiku es el texto
 * "AAAA-MM-DD" (sin hora ni zona).
 *
 * - dateLocalDeDia y diaLocalDeDate pasan del día a un Date local y de vuelta, con
 *   las partes del día y los getters locales (para el DatePicker).
 * - calendarDateToDate da la medianoche UTC del día.
 * - formatearDia escribe el día con Intl en zona UTC, en el formato del idioma.
 *
 * isCalendarDate, diaDeCalendario, calendarDateToDate y compararDias siguen las
 * reglas de src/common/utils/calendarDate.ts del server; formatearDia también, salvo
 * que con algo que no es un día da "" en vez de tirar.
 * Archivo del generador: se reescribe en cada regeneración.
 */

/** Un día de calendario: "AAAA-MM-DD". Sin hora ni zona. */
export type CalendarDate = string;

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH = /^(\d{4})-(\d{2})$/;
/** Un ISO con hora: "AAAA-MM-DDTHH:MM…", con o sin zona al final. */
const ISO_WITH_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
/**
 * Si un ISO termina en una zona ("Z", "+03:00", "-0300", "+03"). Sin zona, se le agrega
 * "Z". El Date de JavaScript no lee "+03" (dos cifras, sin minutos): ese da null.
 */
const ISO_ZONE = /(Z|[+-]\d{2}(:?\d{2})?)$/i;

const pad = (n: number, width: number) => String(n).padStart(width, "0");

/** El Date de la medianoche UTC de (año, mes 1-12, día). setUTCFullYear: los años de 0 a 99 no pasan a 1900. */
function utcMidnight(year: number, month: number, day: number): Date {
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  return date;
}

/** El día UTC de un instante, o null si no es un Date válido o cae fuera de los años 0001 a 9999. */
function utcDayOf(date: Date): CalendarDate | null {
  if (Number.isNaN(date.getTime())) return null;
  const day = `${pad(date.getUTCFullYear(), 4)}-${pad(date.getUTCMonth() + 1, 2)}-${pad(date.getUTCDate(), 2)}`;
  return isCalendarDate(day) ? day : null;
}

/**
 * "AAAA-MM-DD" con un día que existe (rechaza "2026-02-30"), de los años 0001 a 9999.
 *
 * Como CalendarDate es string, con un argumento string la rama negativa queda en never:
 * después de `if (isCalendarDate(texto)) return …;` no se puede seguir usando `texto`.
 * Para separar la forma, la regex (como diaDeCalendario).
 */
export function isCalendarDate(value: unknown): value is CalendarDate {
  if (typeof value !== "string") return false;
  const match = DAY.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1 || month < 1 || month > 12 || day < 1) return false;
  const date = utcMidnight(year, month, day);
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

/** Las tres partes de un día que existe; si no lo es, tira. */
function partsOf(day: CalendarDate): [number, number, number] {
  if (!isCalendarDate(day)) throw new RangeError('no es un día de calendario "AAAA-MM-DD"');
  return [Number(day.slice(0, 4)), Number(day.slice(5, 7)), Number(day.slice(8, 10))];
}

/**
 * Lleva cualquier forma vieja o nueva a "AAAA-MM-DD", como en el server:
 * - Date → su día UTC;
 * - "AAAA-MM-DD" de un día que existe → igual;
 * - ISO con hora → el día UTC del instante (sin zona al final, se toma como UTC);
 * - null, undefined, "", lo que no se puede leer o un día fuera de los años 0001 a
 *   9999 → null.
 */
export function diaDeCalendario(value: Date | string | null | undefined): CalendarDate | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return utcDayOf(value);
  if (typeof value !== "string") return null;
  // Con la forma "AAAA-MM-DD", el día si existe. (No `if (isCalendarDate(value)) return
  // value;`: después de esa guarda `value` queda en never; ver isCalendarDate.)
  if (DAY.test(value)) return isCalendarDate(value) ? value : null;
  if (!ISO_WITH_TIME.test(value) || !isCalendarDate(value.slice(0, 10))) return null;
  return utcDayOf(new Date(ISO_ZONE.test(value) ? value : `${value}Z`));
}

/** El Date de la medianoche UTC de ese día (el de la celda de Excel). Si no es un día, tira. */
export function calendarDateToDate(day: CalendarDate): Date {
  const [year, month, d] = partsOf(day);
  return utcMidnight(year, month, d);
}

/**
 * Para el DatePicker: el Date local de ese día, a las 00:00 (como new Date(a, m - 1, d),
 * sin pasar los años de 0 a 99 a 1900). Si no es un día, tira.
 *
 * Si esa medianoche no existió en la zona del navegador, JavaScript corre la hora hacia
 * adelante: con el cambio de hora de Argentina del 19/10/2008 a las 00:00, da la 01:00
 * de ese día; con un día que la zona salteó entero (Pacific/Kiritimati, 31/12/1994),
 * da el día siguiente.
 */
export function dateLocalDeDia(day: CalendarDate): Date {
  const [year, month, d] = partsOf(day);
  const date = new Date(2000, 0, 1);
  date.setFullYear(year, month - 1, d);
  return date;
}

/** Para el DatePicker: el día que el Date muestra en la zona del navegador (getters locales). */
export function diaLocalDeDate(date: Date): CalendarDate {
  return `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1, 2)}-${pad(date.getDate(), 2)}`;
}

/** -1, 0 o 1, en orden cronológico (que para "AAAA-MM-DD" es el alfabético). */
export function compararDias(a: CalendarDate, b: CalendarDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Un Intl.DateTimeFormat por locale: armarlo cuesta, y formatearDia corre por cada fila de una grilla. */
const formateadores = new Map<string, Intl.DateTimeFormat>();

/** Cómo se escribe un día: en zona UTC, con dos cifras en el día y en el mes. */
const FORMATO_DEL_DIA: Intl.DateTimeFormatOptions = { timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit" };

/**
 * El formateador de un locale. Uno mal escrito no tira (Intl da RangeError y la
 * pantalla se caería): "es_AR" se lee como "es-AR", y lo que ni así es un locale cae a
 * "es-AR".
 */
function formateadorDe(locale: string): Intl.DateTimeFormat {
  for (const candidato of [locale, locale.replace(/_/g, "-")]) {
    try {
      return new Intl.DateTimeFormat(candidato, FORMATO_DEL_DIA);
    } catch {
      // Intl no acepta ese locale: el siguiente.
    }
  }
  return new Intl.DateTimeFormat("es-AR", FORMATO_DEL_DIA);
}

/**
 * Texto para una persona, con Intl en zona UTC, en el locale dado (los de timeFormats:
 * "es-AR", "en-US", "pt-BR"), con dos cifras en el día y en el mes. null, "" o algo que
 * no es un día → "". Un locale mal escrito no tira (formateadorDe).
 */
export function formatearDia(day: CalendarDate | null | undefined, locale: string): string {
  if (!isCalendarDate(day)) return "";
  let formateador = formateadores.get(locale);
  if (formateador === undefined) {
    formateador = formateadorDe(locale);
    formateadores.set(locale, formateador);
  }
  return formateador.format(calendarDateToDate(day));
}

/** Para los filtros: el primer día del año y el del año siguiente. El año, de 1 a 9998; si no, tira. */
export function rangoDeAnio(anio: number): { gte: CalendarDate; lt: CalendarDate } {
  if (!Number.isInteger(anio) || anio < 1 || anio > 9998) throw new RangeError("rangoDeAnio: un año de 1 a 9998");
  return { gte: `${pad(anio, 4)}-01-01`, lt: `${pad(anio + 1, 4)}-01-01` };
}

/** Para los filtros: "AAAA-MM" → el primer día de ese mes y el del mes siguiente. El año, de 0001 a 9998; si no, tira. */
export function rangoDeMes(anioMes: string): { gte: CalendarDate; lt: CalendarDate } {
  const match = MONTH.exec(anioMes);
  const year = match ? Number(match[1]) : 0;
  const month = match ? Number(match[2]) : 0;
  if (!match || year < 1 || year > 9998 || month < 1 || month > 12) throw new RangeError('rangoDeMes: "AAAA-MM", de 0001-01 a 9998-12');
  const next = month === 12 ? `${pad(year + 1, 4)}-01-01` : `${pad(year, 4)}-${pad(month + 1, 2)}-01`;
  return { gte: `${pad(year, 4)}-${pad(month, 2)}-01`, lt: next };
}

/** El día de un instante (ahora, por defecto) en una zona IANA. */
export function hoyEnLaZona(timeZone: string, now: Date = new Date()): CalendarDate {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${pad(Number(part("year")), 4)}-${part("month")}-${part("day")}`;
}
